import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faEdit,
  faUpload,
  IconLookup
} from "@fortawesome/free-solid-svg-icons";
import toast from "@/utils/toast";

import { Project, Task } from "@/api/types";
import { apiClient } from "@/api";
import AppButton from "@/components/structure/AppButton";
import TaskTile from "@/components/tasks/TaskTile";
import IconChooser from "@/components/widgets/IconChooser";
import AssignmentsSection from "@/components/widgets/AssignmentsSection";

export interface EditableProjectProps {
  project?: Project;
  onNameChange?: (name: string) => void;
  onDescriptionChange?: (description: string) => void;
  onIconChange?: (icon: string) => void;
  onTasksChange?: (tasks: Task[]) => void;
}

type ProjectFormValues = {
  name: string;
  description: string;
  icon: string;
  participants: number[];
  participantCohorts: number[];
  facilitators: number[];
  facilitatorCohorts: number[];
};

export default function EditableProject({
  project
}: EditableProjectProps) {
  const navigate = useNavigate();
  const [localTasks, setLocalTasks] = useState<Task[]>(project?.tasks || []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ProjectFormValues>({
    defaultValues: {
      name: project?.name || "",
      description: project?.description || "",
      icon: project?.icon || "",
      participants: project?.participants || [],
      participantCohorts: project?.participantCohorts || [],
      facilitators: project?.facilitators || [],
      facilitatorCohorts: project?.facilitatorCohorts || [],
    },
  });

  const id = project?.id;

  const saveProject = async (id: string, data: Project) => {
    try {
      const response = await apiClient.put(`/projects/${id}/`, data);

      console.log("Project saved successfully: ", response);
      toast.info("Project saved successfully");
    } catch (error) {
      console.error("Error saving project:", error);
      toast.apiError("Failed to save project", error);
    }
  };

  const createProject = async (data: Project) => {
    try {
      const response = await apiClient.post<Project>("/projects/", data);

      console.log("Project created successfully: ", response);
      toast.info("Project created successfully");

      // redirect to the new project
      navigate(`/projects/${response.id}/edit`);
    } catch (error) {
      console.error("Error creating project:", error);
      toast.apiError("Failed to create project", error);
    }
  };

  const createNewTask = () => {
    if (id) {
      navigate(`/projects/${id}/tasks/new`);
    } else {
      toast.error("Project must be saved before creating tasks");
    }
  };

  const editTask = (taskId: string) => {
    navigate(`/tasks/${taskId}/edit`);
  };

  const deleteTask = async (taskId: string) => {
    try {
      if (
        confirm(
          "Are you sure you want to delete this task? This action cannot be undone."
        )
      ) {
        await apiClient.delete(`/tasks/${taskId}/`);
        const newTasks = localTasks.filter((task) => task.id !== taskId);
        setLocalTasks(newTasks);
        toast.info("Task deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.apiError("Failed to delete task", error);
    }
  };

  const persistButton = (
    <AppButton type="submit" variant="success">
      <FontAwesomeIcon icon={faUpload} className="mr-2" />
      {id ? "Save" : "Create"} Project
    </AppButton>
  );

  const formSubmitted = (data: FieldValues) => {
    if (id) {
      saveProject(id, data as Project);
    } else {
      createProject(data as Project);
    }
  }

  return (
    <div className="space-y-6">
      {/* Editable Project Header */}
      <form onSubmit={handleSubmit(formSubmitted)} className="space-y-6">
        <div className="bg-gray-100 p-4 rounded-lg space-y-4">
          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
            Project Configuration
          </h4>

          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Name
            </label>
            <input
              type="text"
              {...register("name", { required: true })}
              className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter project name..."
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1 ml-1">Project name is required</p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Description
            </label>
            <textarea
              {...register("description")}
              rows={3}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
              placeholder="Enter project description..."
            />
          </div>

          {/* Icon Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Icon
            </label>
            <div className="max-w-xs">
              <Controller control={control} name="icon" defaultValue={project?.icon || "question"}
               render={({ field: { onChange, value } }) => (
                <>
                  <IconChooser
                    value={value}
                    onChange={onChange}
                    placeholder="Search icons..."
                  />
                  {value && (
                    <div>
                      <p className="text-sm text-gray-500 mt-2">
                        Selected Icon:{" "}
                        <FontAwesomeIcon
                          icon={{ prefix: "fas", iconName: value } as IconLookup}
                        />{" "}
                        ({value})
                      </p>
                    </div>
                  )}
                </>
               )} />
            </div>
          </div>

          {/* Participant/Facilitator Assignments */}
          <AssignmentsSection
            control={control}
            defaultExpanded={false}
            sectionId="project-assignments-section"
          />
        </div>

        {/* Project Form Actions: Delete, Create/Save */}
        <div className="flex justify-end">
          { project?.id && (
            <AppButton
              type="button"
              variant="danger"
              className="mr-2"
              onClick={async () => {
                if (
                  confirm(
                    "Are you sure you want to delete this project? This action cannot be undone."
                  )
                ) {
                  try {
                    await apiClient.delete(`/projects/${project.id}/`);
                    toast.info("Project deleted successfully");
                    navigate("/projects");
                  } catch (error) {
                    console.error("Error deleting project:", error);
                    toast.apiError("Failed to delete project", error);
                  }
                }
              }}
            >
              <FontAwesomeIcon icon={faTrash} className="mr-2" />
              Delete Project
            </AppButton>
          )}

          {persistButton}
        </div>
      </form>

      {/* Tasks Section */}
      {id && (
        <div className="bg-gray-100 p-4 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
              Tasks ({localTasks.length})
            </h4>
            <button
              onClick={createNewTask}
              className="flex items-center space-x-2 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              disabled={!id}
              title={
                !id ? "Save project first to create tasks" : "Create new task"
              }
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>New Task</span>
            </button>
          </div>

          {/* Tasks List */}
          {localTasks.length === 0 ? (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <p className="text-gray-500 text-lg">No tasks yet</p>
              <p className="text-gray-400 text-sm mt-2 mb-4">
                Create your first task to get started
              </p>
              <button
                onClick={createNewTask}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 mx-auto"
                disabled={!id}
                title={
                  !id ? "Save project first to create tasks" : "Create new task"
                }
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Create First Task</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {localTasks.map((task) => (
                <TaskTile key={task.id} task={task} skipDependencyCheck={true} showUserProgress={false} showEngagement={false}>
                  <div className="flex items-center space-x-2 mt-2">
                    <AppButton
                      variant="primary"
                      onClick={() => editTask(task.id)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-2" /> Edit
                      Task
                    </AppButton>
                    <AppButton
                      variant="danger"
                      onClick={() => deleteTask(task.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="mr-2" /> Delete
                      Task
                    </AppButton>
                  </div>
                </TaskTile>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
